import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { useRootStore } from "../../../../store/contexts";
import { CrudRows } from "../../../common/types";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { ShowMultiLine } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { ShowDateTime } from "../../../common/controls";
import { PaymentReceiptStatus } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";

export type PaymentReceiptViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  paymentReceiptFiles: CrudRows;
  paymentReceiptViewAction?: IMenuAction[];
  crcValue?: { displayId: string; id: string } | null;
  crc: string;
  typeValue: { displayId: string; id: string } | null;
  amountValue?: { displayId: string; id: string } | null;
  amount: number;
  issueDateTimeValue?: { displayId: string; id: string } | null;
  issueDateTime: Date | null;
  statusOptions: { id: string; displayId: string }[];
  status: PaymentReceiptStatus;
  totalUsesValue?: { displayId: string; id: string } | null;
  totalUses: number;
  remainingUsesValue?: { displayId: string; id: string } | null;
  remainingUses: number;
  usedDateValue?: { displayId: string; id: string } | null;
  usedDate: Date | null;
  signedEditionValue: { displayId: string; id: string } | null;
  usesRows: CrudRows;
  usesVisibleColumns: GridColumn[];
  usesSetColumnWiths: (x: ColumnWidthInfo[]) => void;
};

export const PaymentReceiptViewForm: React.FC<PaymentReceiptViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      paymentReceiptFiles,
      paymentReceiptViewAction,
      crc = "",
      typeValue,
      amount = 0,
      issueDateTime = null,
      statusOptions = [],
      status = "" as PaymentReceiptStatus,
      totalUses = 0,
      remainingUses = 0,
      usedDate = null,
      signedEditionValue,
      usesRows,
      usesVisibleColumns,
      usesSetColumnWiths,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="null"
              title={langStore.groupPaymentReceiptLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <ShowFilesCompact
                  rows={paymentReceiptFiles}
                  showLabel={true}
                  label={langStore.PaymentReceiptPaymentReceiptLabel}
                  menuActions={paymentReceiptViewAction}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                />
                <ShowMultiLine
                  value={crc}
                  label={langStore.PaymentReceiptCrcLabel}
                />
                <ShowReference
                  label={langStore.PaymentReceiptTypeLabel}
                  selectedValue={langStore.displayIdByLang(
                    typeValue?.displayId
                  )}
                />
                <ShowNumber
                  value={amount}
                  label={langStore.PaymentReceiptAmountLabel}
                  decimalScale={2}
                />
                <ShowDateTime
                  value={issueDateTime}
                  label={langStore.PaymentReceiptIssueDateTimeLabel}
                />
                <ShowString
                  value={
                    status
                      ? statusOptions.find(
                          (o: { id: string }) => o.id === status
                        )?.displayId || status
                      : ""
                  }
                  label={langStore.PaymentReceiptStatusLabel}
                />
                <ShowNumber
                  value={totalUses}
                  label={langStore.PaymentReceiptTotalUsesLabel}
                />
                <ShowNumber
                  value={remainingUses}
                  label={langStore.PaymentReceiptRemainingUsesLabel}
                />
                <ShowDateTime
                  value={usedDate}
                  label={langStore.PaymentReceiptUsedDateLabel}
                />
                <ShowReference
                  label={langStore.PaymentReceiptSignedEditionLabel}
                  selectedValue={langStore.displayIdByLang(
                    signedEditionValue?.displayId
                  )}
                />
                <ShowItemsCompact
                  rows={usesRows}
                  showLabel={true}
                  label={langStore.PaymentReceiptUsesLabel}
                  columns={usesVisibleColumns}
                  setColumnWidths={usesSetColumnWiths}
                  entityName="PaymentReceiptUse"
                  enableDownload={true}
                  onRowClick={(rowId) =>
                    rootStore.openSubentityModal("paymentReceiptUse", rowId)
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
